import React, { useMemo, useState } from 'react';
import { Check, Loader2, Clapperboard, Link2, Sparkles, Play, Clock, Film, AlertTriangle, Lock, GripVertical, Maximize2 } from 'lucide-react';

// Vue « flux » du Réalisateur IA (style make.com) : une carte par scène, des flèches entre les scènes.
//  - une scène NOUVELLE démarre seule, dès qu'elle est validée (plusieurs peuvent partir en même temps) ;
//  - une scène ENCHAÎNÉE (suite) attend la dernière image de la scène d'avant ;
//  - toutes les séquences se rejoignent dans le nœud « Assemblage ».
//  - glisser-déposer : on déplace une carte pour changer l'ordre des scènes (avant le lancement).
const W = 252;      // largeur d'une carte
const H = 262;      // hauteur d'une carte
const GX = 84;      // espace entre deux cartes d'une séquence (la flèche)
const GY = 56;      // espace entre deux séquences
const TRIG = 132;   // largeur de la zone « Lancement »
const FIN = 176;    // largeur de la zone « Assemblage »
const PAD = 22;
const LABEL = 24;   // hauteur du titre de séquence
const ANCHOR = 78;  // hauteur (depuis le haut de la carte) où les flèches s'accrochent

const STATES = {
  draft: { chip: 'bg-amber-500/15 text-amber-200 border-amber-400/40', glow: '251,191,36', bar: '#f59e0b' },
  ready: { chip: 'bg-purple-500/20 text-purple-100 border-purple-400/50', glow: '168,85,247', bar: '#a855f7' },
  waiting: { chip: 'bg-slate-500/20 text-slate-200 border-slate-400/40', glow: '148,163,184', bar: '#64748b' },
  queued: { chip: 'bg-amber-500/20 text-amber-100 border-amber-300/60', glow: '251,191,36', bar: '#fbbf24' },
  completed: { chip: 'bg-emerald-500/20 text-emerald-100 border-emerald-400/50', glow: '16,185,129', bar: '#10b981' },
  failed: { chip: 'bg-red-500/20 text-red-100 border-red-400/60', glow: '239,68,68', bar: '#ef4444' },
};

const curve = (x1, y1, x2, y2) => {
  const dx = Math.max(24, Math.abs(x2 - x1) * 0.5);
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
};

const clamp = (n) => ({ display: '-webkit-box', WebkitLineClamp: n, WebkitBoxOrient: 'vertical', overflow: 'hidden' });

export default function FilmFlow({
  scenes = [], nodes = null, providers = {}, priceOf = () => 0, formatAr = (v) => v,
  selected = null, onSelect, onToggleValidate, onToggleChain, onReorder, reorderable = false,
  final = {}, notes = {},
}) {
  const [dragFrom, setDragFrom] = useState(null);
  const [drop, setDrop] = useState(null);   // { index, after }

  const layout = useMemo(() => {
    const lanes = [];
    scenes.forEach((s, i) => {
      const chain = i > 0 && !!s.uses_previous_frame;
      if (chain && lanes.length) lanes[lanes.length - 1].push(i); else lanes.push([i]);
    });
    const maxLen = Math.max(1, ...lanes.map((l) => l.length));
    const laneH = H + GY;
    const bodyH = lanes.length * laneH - GY;
    const totalH = PAD * 2 + LABEL + bodyH;
    const x0 = PAD + TRIG + GX;
    const pos = {};
    lanes.forEach((lane, li) => lane.forEach((idx, j) => { pos[idx] = { x: x0 + j * (W + GX), y: PAD + LABEL + li * laneH, lane: li, j }; }));
    const xFinal = x0 + maxLen * (W + GX);
    return { lanes, pos, totalW: xFinal + FIN + PAD, totalH, xFinal, trigY: PAD + LABEL + bodyH / 2 - 44, finY: PAD + LABEL + bodyH / 2 - 52 };
  }, [scenes]);

  const stateOf = (i) => {
    if (nodes && nodes[i]) return nodes[i].state;
    const s = scenes[i] || {};
    if (!s.validated) return 'draft';
    return i > 0 && s.uses_previous_frame ? 'waiting' : 'ready';
  };
  const nodeOf = (i) => (nodes && nodes[i]) || {};
  const editable = (i) => ['draft', 'ready', 'waiting'].includes(stateOf(i));
  const label = (i) => {
    const st = stateOf(i);
    if (st === 'draft') return 'À vérifier';
    if (st === 'ready') return nodes ? 'Prête · part au prochain pas' : 'Validée · part au lancement';
    if (st === 'waiting') return `Validée · attend la scène ${i}`;
    if (st === 'queued') return 'En génération…';
    if (st === 'completed') return 'Terminée';
    return 'Échouée';
  };

  const total = scenes.length;
  const validatedCount = scenes.filter((s, i) => (nodes && nodes[i] ? nodes[i].validated : s.validated)).length;
  const doneCount = nodes ? nodes.filter((n) => n.state === 'completed').length : 0;
  const trigRight = PAD + TRIG - 14;
  const trigMid = layout.trigY + 44;
  const finMid = layout.finY + 52;

  const paths = [];
  layout.lanes.forEach((lane, li) => {
    const head = layout.pos[lane[0]];
    paths.push({ d: curve(trigRight, trigMid, head.x, head.y + ANCHOR), color: '#a855f7', dashed: true, active: stateOf(lane[0]) === 'queued', key: `t${li}` });
    lane.forEach((idx, j) => {
      if (j === 0) return;
      const a = layout.pos[lane[j - 1]];
      const b = layout.pos[idx];
      paths.push({ d: curve(a.x + W, a.y + ANCHOR, b.x, b.y + ANCHOR), color: '#34d399', dashed: false, active: stateOf(idx) === 'queued', key: `c${idx}`, chainLabel: { x: (a.x + W + b.x) / 2, y: a.y + ANCHOR } });
    });
    const tail = layout.pos[lane[lane.length - 1]];
    paths.push({ d: curve(tail.x + W, tail.y + ANCHOR, layout.xFinal, finMid), color: '#f5b942', dashed: true, active: false, key: `f${li}` });
  });

  const endDrag = () => { setDragFrom(null); setDrop(null); };

  return (
    <div>
      <style>{`
        @keyframes ffdash{to{stroke-dashoffset:-14}}
        .ff-flow{animation:ffdash .9s linear infinite}
        @keyframes ffpulse{0%,100%{box-shadow:0 0 0 0 rgba(251,191,36,.35)}50%{box-shadow:0 0 0 9px rgba(251,191,36,0)}}
        .ff-pulse{animation:ffpulse 1.8s ease-in-out infinite}
        @keyframes ffshine{0%{transform:translateX(-120%)}100%{transform:translateX(220%)}}
        .ff-card{transition:left .55s cubic-bezier(.22,1,.36,1),top .55s cubic-bezier(.22,1,.36,1),transform .28s cubic-bezier(.22,1,.36,1),box-shadow .28s,opacity .2s;transform-style:preserve-3d;will-change:transform}
        .ff-card:hover{transform:perspective(1100px) translateY(-6px) rotateX(2deg) scale(1.012)}
        .ff-card.ff-dragging{opacity:.4;transform:scale(.96) rotate(-1.5deg)}
        .ff-card .ff-shine{position:absolute;inset:0;overflow:hidden;border-radius:inherit;pointer-events:none}
        .ff-card .ff-shine:after{content:"";position:absolute;top:0;bottom:0;width:45%;background:linear-gradient(100deg,transparent,rgba(255,255,255,.10),transparent);transform:translateX(-120%)}
        .ff-card:hover .ff-shine:after{animation:ffshine .9s ease}
        .ff-grip{cursor:grab}.ff-grip:active{cursor:grabbing}
      `}</style>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-2.5 text-[10px] text-gray-400">
        <span className="inline-flex items-center gap-1.5"><span className="w-5 h-0 border-t-2 border-dashed border-purple-400" /> Nouvelle scène : part seule, dès sa validation</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-5 h-0 border-t-2 border-emerald-400" /> Suite : attend la dernière image de la scène d&apos;avant</span>
        {reorderable && <span className="inline-flex items-center gap-1.5 text-fuchsia-300"><GripVertical className="w-3 h-3" /> Glissez une carte pour changer l&apos;ordre</span>}
        <span className="ml-auto font-semibold text-gray-300">{validatedCount}/{total} validée{validatedCount > 1 ? 's' : ''}{nodes ? ` · ${doneCount} terminée${doneCount > 1 ? 's' : ''}` : ''}</span>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#0d0719]" style={{ backgroundImage: 'radial-gradient(rgba(168,85,247,0.16) 1px, transparent 1px)', backgroundSize: '22px 22px' }}>
        <div className="relative" style={{ width: layout.totalW, height: layout.totalH, minWidth: '100%' }}>
          <svg width={layout.totalW} height={layout.totalH} className="absolute inset-0 pointer-events-none">
            <defs>
              {[['p', '#a855f7'], ['g', '#34d399'], ['a', '#f5b942']].map(([k, c]) => (
                <marker key={k} id={`ffarrow-${k}`} markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto"><path d="M0,0 L9,4.5 L0,9 z" fill={c} /></marker>
              ))}
            </defs>
            {paths.map((p) => (
              <path key={p.key} d={p.d} fill="none" stroke={p.color} strokeWidth="2.2" strokeLinecap="round" strokeDasharray={p.dashed || p.active ? '7 7' : undefined} className={p.active ? 'ff-flow' : ''}
                markerEnd={`url(#ffarrow-${p.color === '#a855f7' ? 'p' : p.color === '#34d399' ? 'g' : 'a'})`} opacity={p.dashed ? 0.8 : 1} />
            ))}
          </svg>

          {paths.filter((p) => p.chainLabel).map((p) => (
            <div key={`l${p.key}`} className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{ left: p.chainLabel.x, top: p.chainLabel.y - 15 }}>
              <span className="inline-flex items-center gap-1 text-[9px] font-bold text-emerald-200 bg-emerald-900/70 border border-emerald-400/40 rounded-full px-1.5 py-0.5"><Link2 className="w-2.5 h-2.5" /> suite</span>
            </div>
          ))}

          {/* Lancement */}
          <div className="absolute flex flex-col items-center text-center" style={{ left: PAD, top: layout.trigY, width: TRIG - 14 }}>
            <div className="w-[68px] h-[68px] rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#9333EA,#DB2777)', boxShadow: '0 0 28px rgba(168,85,247,0.6), inset 0 2px 0 rgba(255,255,255,.25)' }}>
              <Play className="w-7 h-7 text-white" />
            </div>
            <div className="text-white text-[11px] font-black mt-2">Lancement</div>
            <div className="text-[9px] text-gray-400 leading-snug">{layout.lanes.length} séquence{layout.lanes.length > 1 ? 's' : ''} en parallèle</div>
          </div>

          {/* Séquences */}
          {layout.lanes.map((lane, li) => {
            const head = layout.pos[lane[0]];
            return (
              <div key={`lane${li}`} className="absolute text-[10px] font-bold uppercase tracking-wider text-purple-300/80 transition-all duration-500" style={{ left: head.x, top: head.y - 20 }}>
                Séquence {li + 1} <span className="normal-case font-medium text-gray-500 tracking-normal">· {lane.length > 1 ? `${lane.length} scènes enchaînées` : 'scène indépendante'}</span>
              </div>
            );
          })}

          {/* Cartes */}
          {scenes.map((s, i) => {
            const pos = layout.pos[i];
            if (!pos) return null;
            const st = stateOf(i);
            const nd = nodeOf(i);
            const meta = STATES[st] || STATES.draft;
            const chain = i > 0 && !!s.uses_previous_frame;
            const cfg = providers[s.engine];
            const canEdit = editable(i);
            const canDrag = reorderable && canEdit;
            const validated = nodes && nodes[i] ? nodes[i].validated : !!s.validated;
            const hue = (i * 47) % 360;
            const thumb = nd.video_url ? 'video' : (nd.last_frame_url || nd.start_image_url) ? 'img' : 'none';
            const isDrop = drop && drop.index === i && dragFrom !== null && dragFrom !== i;
            return (
              <div key={`card${i}`}
                draggable={canDrag}
                onDragStart={(e) => { if (!canDrag) return; e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(i)); } catch (err) { /* ok */ } setDragFrom(i); }}
                onDragEnd={endDrag}
                onDragOver={(e) => {
                  if (dragFrom === null || !reorderable) return;
                  e.preventDefault();
                  const r = e.currentTarget.getBoundingClientRect();
                  const after = e.clientX > r.left + r.width / 2;
                  if (!drop || drop.index !== i || drop.after !== after) setDrop({ index: i, after });
                }}
                onDrop={(e) => {
                  if (dragFrom === null || !reorderable) return;
                  e.preventDefault();
                  const after = drop && drop.index === i ? drop.after : false;
                  const from = dragFrom;
                  endDrag();
                  if (from !== i && onReorder) onReorder(from, i, after);
                }}
                onClick={() => onSelect && onSelect(i)}
                className={`ff-card absolute rounded-2xl border flex flex-col cursor-pointer ${dragFrom === i ? 'ff-dragging' : ''} ${st === 'queued' ? 'ff-pulse' : ''}`}
                style={{
                  left: pos.x, top: pos.y, width: W, height: H,
                  background: 'linear-gradient(160deg, rgba(36,15,66,.97), rgba(15,7,30,.98))',
                  borderColor: selected === i ? 'rgba(232,121,249,.9)' : `rgba(${meta.glow},.45)`,
                  borderStyle: st === 'waiting' ? 'dashed' : 'solid',
                  boxShadow: `0 10px 30px rgba(0,0,0,.45), 0 0 22px rgba(${meta.glow},.12), inset 0 1px 0 rgba(255,255,255,.07)${selected === i ? ', 0 0 0 2px rgba(232,121,249,.55)' : ''}`,
                }}>
                <span className="ff-shine" />
                <span className="absolute left-0 top-4 bottom-4 w-[3px] rounded-r-full" style={{ background: meta.bar, boxShadow: `0 0 10px ${meta.bar}` }} />
                {isDrop && <span className="absolute top-2 bottom-2 w-1.5 rounded-full bg-fuchsia-400" style={{ [drop.after ? 'right' : 'left']: -GX / 2 + 6, boxShadow: '0 0 18px 4px rgba(232,121,249,.8)' }} />}

                <div className="flex items-center gap-2 px-3 pt-2.5">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-black flex-shrink-0" style={{ background: 'linear-gradient(135deg,#9333EA,#DB2777)', boxShadow: '0 0 12px rgba(168,85,247,.55), inset 0 1px 0 rgba(255,255,255,.3)' }}>{i + 1}</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-bold text-purple-200 truncate">{cfg?.label || s.engine}</div>
                    <div className="text-[9px] text-gray-500">{s.duration}s · {s.resolution} · <b className="text-amber-300">{formatAr(priceOf(s))}</b></div>
                  </div>
                  {canDrag && <span className="ff-grip text-gray-500 hover:text-fuchsia-300 transition" title="Glisser pour déplacer"><GripVertical className="w-4 h-4" /></span>}
                </div>

                <div className="px-3 mt-1.5">
                  {i === 0 ? (
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold text-sky-200 bg-sky-500/10 border border-sky-400/30 rounded-full px-2 py-0.5"><Sparkles className="w-2.5 h-2.5" /> Première scène</span>
                  ) : (
                    <button type="button" disabled={!canEdit || !onToggleChain} onClick={(e) => { e.stopPropagation(); onToggleChain && onToggleChain(i); }}
                      title={canEdit ? (chain ? 'Cliquer : en faire une nouvelle scène (part seule)' : 'Cliquer : en faire la suite de la scène précédente') : ''}
                      className={`inline-flex items-center gap-1 text-[9px] font-bold rounded-full px-2 py-0.5 border transition ${chain ? 'text-emerald-200 bg-emerald-500/10 border-emerald-400/40' : 'text-sky-200 bg-sky-500/10 border-sky-400/30'} ${canEdit ? 'hover:brightness-125' : 'cursor-default'}`}>
                      {chain ? <><Link2 className="w-2.5 h-2.5" /> Suite de la scène {i}</> : <><Sparkles className="w-2.5 h-2.5" /> Nouvelle scène · part seule</>}
                    </button>
                  )}
                </div>

                <div className="px-3 mt-2">
                  <div className="relative h-[88px] rounded-xl overflow-hidden border border-white/10" style={thumb === 'none' ? { background: `linear-gradient(135deg, hsl(${hue} 60% 26%), hsl(${(hue + 55) % 360} 65% 12%))` } : { background: '#000' }}>
                    {thumb === 'video' && <video src={`${nd.video_url}#t=0.4`} muted playsInline preload="metadata" className="w-full h-full object-cover" />}
                    {thumb === 'img' && <img src={nd.last_frame_url || nd.start_image_url} alt="" className="w-full h-full object-cover opacity-80" />}
                    {thumb === 'none' && (
                      <div className="w-full h-full flex flex-col items-center justify-center px-2 text-center">
                        <Clapperboard className="w-6 h-6 text-white/30 mb-1" />
                        <span className="text-[9px] text-white/65 leading-tight" style={clamp(2)}>{s.shot || 'Plan à vérifier'}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,.07), transparent 40%, rgba(0,0,0,.35))' }} />
                    {st === 'queued' && <div className="absolute inset-0 bg-black/55 flex items-center justify-center gap-1.5 text-amber-100 text-[10px] font-bold"><Loader2 className="w-4 h-4 animate-spin" /> Génération…</div>}
                    {st === 'failed' && <div className="absolute inset-0 bg-red-950/70 flex items-center justify-center gap-1.5 text-red-100 text-[10px] font-bold"><AlertTriangle className="w-4 h-4" /> Échouée</div>}
                    {st === 'completed' && <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                    <div className="absolute bottom-1 right-1 w-5 h-5 rounded-md bg-black/50 flex items-center justify-center opacity-70"><Maximize2 className="w-3 h-3 text-white" /></div>
                  </div>
                  <p className="text-[10px] text-gray-300 leading-snug mt-1.5" style={clamp(2)}>{s.prompt_fr || s.prompt}</p>
                </div>

                <div className="mt-auto px-3 pb-2.5">
                  <div className={`text-[9px] font-bold text-center rounded-md border px-1.5 py-0.5 mb-1.5 ${meta.chip}`}>
                    {st === 'waiting' && <Clock className="w-2.5 h-2.5 inline -mt-0.5 mr-1" />}{label(i)}
                  </div>
                  {canEdit ? (
                    <button type="button" onClick={(e) => { e.stopPropagation(); onToggleValidate && onToggleValidate(i); }}
                      className={`w-full inline-flex items-center justify-center gap-1 py-1.5 rounded-lg text-[10px] font-bold text-white transition ${validated ? 'bg-emerald-600/70 hover:bg-emerald-600 border border-emerald-400/50' : 'border border-purple-400/40 hover:brightness-110'}`}
                      style={validated ? undefined : { background: 'linear-gradient(135deg,#9333EA,#DB2777)' }}>
                      <Check className="w-3 h-3" /> {validated ? 'Validée' : 'Valider cette scène'}
                    </button>
                  ) : (
                    <span className="w-full inline-flex items-center justify-center gap-1 py-1.5 rounded-lg text-[10px] font-bold text-gray-400 bg-white/5 border border-white/10"><Lock className="w-3 h-3" /> Lancée · cliquer pour voir</span>
                  )}
                  {notes[String(i)] && <p className="text-[9px] text-red-300 leading-snug mt-1" style={clamp(2)}>{notes[String(i)]}</p>}
                </div>
              </div>
            );
          })}

          {/* Assemblage */}
          <div className="absolute flex flex-col items-center text-center" style={{ left: layout.xFinal, top: layout.finY, width: FIN }}>
            <div className={`w-[84px] h-[84px] rounded-2xl flex items-center justify-center border ${final.done ? 'border-emerald-400/60' : 'border-amber-300/50'}`} style={{ background: final.done ? 'linear-gradient(135deg,#059669,#10B981)' : 'linear-gradient(135deg,#7c4a03,#f5b942)', boxShadow: '0 0 28px rgba(245,185,66,0.38), inset 0 2px 0 rgba(255,255,255,.25)' }}>
              {final.montage ? <Loader2 className="w-8 h-8 text-white animate-spin" /> : final.done ? <Check className="w-9 h-9 text-white" /> : <Film className="w-8 h-8 text-white" />}
            </div>
            <div className="text-white text-[11px] font-black mt-2">{final.done ? 'Film prêt' : final.montage ? 'Montage pro…' : 'Assemblage final'}</div>
            <div className="text-[9px] text-gray-400 leading-snug px-2">{final.done ? 'Titre, fondus, musique et générique' : `${doneCount}/${total} scènes terminées · remises dans l'ordre du film`}</div>
          </div>
        </div>
      </div>
      {notes._global && <div className="mt-2.5 text-[11px] text-amber-100 bg-amber-500/10 border border-amber-400/30 rounded-xl px-3 py-2">{notes._global}</div>}
    </div>
  );
}
